import { useState, useEffect } from "react";
import Select from "react-select";
import { Formik, Form, Field } from "formik";
import { number, object, string, date } from "yup";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { CircularProgress } from "@mui/material";
import CloseIcon from "../../../assets/hospitalCloseIcon.svg";
import RightArrow from "../../../assets/rightArrowWhite.svg";
import HospitalReferenceDocument from "../hospitalReferenceDocument/HospitalReferenceDocument";
import { useNavigate } from "react-router-dom";
const errorValidation = object({
  fromDate: date().required("Required Field"),
});

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  return (
    now.getFullYear() +
    "-" +
    pad(now.getMonth() + 1) +
    "-" +
    pad(now.getDate()) +
    "T" +
    pad(now.getHours()) +
    ":" +
    pad(now.getMinutes())
  );
};

const formatDateTimeLocal = (date) => {
  const pad = (n) => n.toString().padStart(2, "0");
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes())
  );
};

const DialysisEnhancementFormHospitalPersona = ({
  totalPackageCost,
  setAddEnhancement,
  caseDetails,
  caseId,
  admissionDate,
  currentSubMasterStageId,
  AllCases,
  enhancementList = [],
  caseProcedures = [],
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [latestEnhancement, setLatestEnhancement] = useState(null);
  const [tierCost, setTierCost] = useState([]);
  const [openReferenceModal, setOpenReferenceModal] = useState(false);
  const navigate = useNavigate();
  const initialValues = {
    days: "",
    fromDate: getCurrentDateTimeLocal(),
    toDate: "",
    ward: "",
    procedureId:
      caseProcedures?.length > 0
        ? caseProcedures[0]?.id
        : caseDetails?.caseprocedures?.[0]?.id || "",
  };

  const handleDateChange = (e, setFieldValue, values) => {
    const fromDate = e.target.value;
    setFieldValue("fromDate", fromDate);
    if (values.days) {
      const toDate = new Date(fromDate);
      toDate.setDate(toDate.getDate() + Number(values.days) - 1);
      setFieldValue("toDate", formatDateTimeLocal(toDate));
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    const data = [
      {
        detailType: "ENHANCEMENT",
        caseId: caseId,
        currentSubMasterStageId: currentSubMasterStageId,
        details: {
          procedureId: values.procedureId,
          enhancementDate: new Date(),
          fromDate: new Date(values.fromDate).toISOString(),
          toDate: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
          enhancementDays: 1,
          cost: Number(caseDetails?.procedures[0]?.tierCost),
          enhancementPerDayCost: Number(caseDetails?.procedures[0]?.tierCost),
        },
        status: 1,
        version: 0,
      },
    ];

    try {
      await CaseServices.addEnhancement(data);
      //   await AllCases();
      navigate(`/dashboard/case-status/${caseId}`);
      setAddEnhancement(false);
    } catch (error) {
      if (error?.response?.status === 420) {
        window.alert(
          "Enhancement documents are not yet mapped. Please perform Initiate Enhancement process when documents are mapped.",
        );
      } else {
        handleApiErrorResponses(error);
      }
    }
    setIsSubmitting(false);
  };

  const getLatestCaseStage = async () => {
    try {
      const response = await CaseServices.getLatestCaseStage(caseId);
      if (response?.data?.currnetSubMasterStage[0]) {
        setLatestEnhancement(response?.data?.latestEnhancement);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const getProcedureCostList = async (procedureId) => {
    const selectedProcedure =
      caseDetails?.caseprocedures?.find((p) => p.id === procedureId) ||
      caseProcedures.find((p) => p.id === procedureId);
    if (!selectedProcedure) return;
    try {
      const response = await CaseServices.getProcedureCostList(
        selectedProcedure.compositId,
        caseDetails?.patient?.city,
      );
      if (response?.data?.procedureCost?.tierCost) {
        setTierCost(response.data.procedureCost.tierCost);
      } else {
        setTierCost([]);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    getLatestCaseStage();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="fixed top-0 left-0 w-screen h-screen flex justify-center items-center bg-[#017C7180]">
      <div className="relative h-fit w-[50%] bg-white p-[30px] rounded-[30px] overflow-y-auto max-h-[90vh]">
        <div
          className="absolute top-1 right-2 cursor-pointer"
          onClick={() => setAddEnhancement(false)}
        >
          <img src={CloseIcon} alt="close-icon" />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={errorValidation}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form className="h-[100%]">
              <div className="w-full flex justify-center items-center">
                <div className="text-[40px] font-medium text-black -tracking-[7%]">
                  Initiate Enhancement{" "}
                  {String((caseDetails?.enhancementCount || 0) + 1).padStart(
                    2,
                    "0",
                  )}
                </div>
              </div>
              <div
                style={{ borderTop: "1px solid #969696", marginBlock: "25px" }}
              />
              <div className="h-[75%]">
                <div className="h-[100%] overflow-y-auto flex flex-wrap gap-y-10">
                  <div className="w-full">
                    <div className="text-sm text-[#0B0B0B] mb-4">
                      1. Please Select Procedure
                    </div>

                    <Select
                      name="procedureId"
                      placeholder="Select Procedure"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "100px",
                        }),
                      }}
                      options={
                        caseProcedures.length
                          ? caseProcedures
                          : caseDetails?.caseprocedures
                      }
                      getOptionLabel={(options) => options.procedure}
                      getOptionValue={(options) => options.id}
                      value={
                        (caseProcedures.length
                          ? caseProcedures
                          : caseDetails?.caseprocedures
                        )?.find((item) => item.id === values.procedureId) ||
                        null
                      }
                      onChange={(item) => {
                        setFieldValue("procedureId", item.id);
                        getProcedureCostList(item.id);
                      }}
                    />
                  </div>

                  <div className="w-full ">
                    <div className="text-sm text-[#0B0B0B] mb-4">
                      2. From which date-time to which date-time should the
                      enhancement last?
                    </div>

                    <div className="w-[75%] grid grid-cols-2 gap-6 mr-2">
                      <div>
                        <Field
                          type="datetime-local"
                          name="fromDate"
                          value={values.fromDate}
                          min={
                            caseDetails?.submasterstagestatuses?.[0]?.commandCenterStage
                              ?.toLowerCase()
                              .includes("enhancement rejected")
                              ? formatDateTimeLocal(
                                  new Date(
                                    enhancementList?.length > 0
                                      ? enhancementList[0]?.details?.fromDate ||
                                          admissionDate
                                      : admissionDate,
                                  ),
                                )
                              : formatDateTimeLocal(
                                  latestEnhancement?.length > 0
                                    ? new Date(
                                        new Date(
                                          new Date(
                                            latestEnhancement[0]?.details
                                              ?.toDate,
                                          ).setDate(
                                            new Date(
                                              latestEnhancement[0]?.details
                                                ?.toDate,
                                            ).getDate() + 1,
                                          ),
                                        ).setHours(0, 0, 0, 0),
                                      )
                                    : new Date(),
                                )
                          }
                          max={getCurrentDateTimeLocal()}
                          style={{
                            border: "1px solid #E4E4E4",
                            width: "100%",
                          }}
                          className="rounded-full py-[10px] px-2"
                          onChange={(e) =>
                            handleDateChange(e, setFieldValue, values)
                          }
                        />
                        {touched.fromDate && errors.fromDate && (
                          <div className="field-error">{errors.fromDate}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  {isSubmitting ? (
                    <CircularProgress />
                  ) : (
                    <button
                      type="submit"
                      style={{ border: "1px solid #017C71" }}
                      className="w-fit flex items-center gap-2 bg-[#017C71] text-white rounded-full mt-3 px-5 py-4 border-0 cursor-pointer"
                    >
                      SAVE DETAILS
                    </button>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>

        {openReferenceModal && (
          <HospitalReferenceDocument
            setOpen={setAddEnhancement}
            selectedCase={caseDetails}
            AllCases={AllCases}
            type="ENHANCEMENTDOCUMENT"
          />
        )}
      </div>
    </div>
  );
};

export default DialysisEnhancementFormHospitalPersona;
